import React, { useMemo } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import IconButton from "@/components/button/IconButton";
import GenerationPeriodLabel from "@/components/layouts/GenerationPeriodLabel.tsx";
import { formatDayLabel, getWeeksBetweenDates } from "@/utils/schedulerUtils";
import { useLazyLoadWeeks } from "@/hooks/useLazyLoadWeeks";
import type { CalendarModalShellProps, ShellDayCell } from "./CalendarModalShell.types";

const CalendarModalShell: React.FC<CalendarModalShellProps> = (
    {
        open,
        onClose,
        title,
        startDate,
        endDate,
        holidays,
        onPrev,
        onNext,
        hasPrev,
        hasNext,
        headerActions,
        renderDayCell,
        strongGrid = false,
        emptyLabel = "Aucune période définie.",
        isEmpty = false,
        containerRef,
        overlay,
    }) => {
    const holidaySet = useMemo(() => new Set(holidays ?? []), [holidays]);

    const { weeks, dateRange } = useMemo(() => {
        if (!startDate || !endDate) {
            return { weeks: [] as Date[], dateRange: "" };
        }

        return {
            weeks: getWeeksBetweenDates(parseISO(startDate), parseISO(endDate)),
            dateRange: `${startDate} à ${endDate}`,
        };
    }, [startDate, endDate]);

    const allWeekRows = useMemo(() => {
        return weeks.map((weekStart) => {
            const days: ShellDayCell[] = [];

            for (let i = 0; i < 7; i++) {
                const date = new Date(weekStart);
                date.setDate(date.getDate() + i);
                const dateStr = format(date, "yyyy-MM-dd");

                days.push({
                    date,
                    dateStr,
                    dayLabel: formatDayLabel(date),
                    isHoliday: holidaySet.has(dateStr),
                });
            }

            return { weekStart, days };
        });
    }, [weeks, holidaySet]);

    const {
        visibleWeeksCount,
        scrollContainerRef,
        sentinelRef,
        hasMoreWeeks,
    } = useLazyLoadWeeks(allWeekRows.length, open);

    const visibleWeekRows = useMemo(
        () => allWeekRows.slice(0, visibleWeeksCount),
        [allWeekRows, visibleWeeksCount]
    );

    if (!open) return null;

    return (
        <div ref={containerRef} className="fixed inset-0 z-50 bg-white flex flex-col" role="dialog" aria-modal="true"
             aria-label={title}>
            <div className="shrink-0 border-b border-gray-200 px-6 py-4">
                <div className="flex items-center gap-4">
                    <IconButton
                        icon={<ArrowLeft size={24}/>}
                        ariaLabel="Fermer"
                        onClick={onClose}
                    />
                    <div className="flex-1">
                        <div className="flex items-center justify-center gap-3 mb-2">
                            {onPrev && (
                                <IconButton
                                    icon={<ChevronLeft size={28}/>}
                                    ariaLabel="Précédent"
                                    onClick={onPrev}
                                    disabled={!hasPrev}
                                />
                            )}
                            <h1 className="text-2xl font-light" aria-live="polite">{title}</h1>
                            {onNext && (
                                <IconButton
                                    icon={<ChevronRight size={28}/>}
                                    ariaLabel="Suivant"
                                    onClick={onNext}
                                    disabled={!hasNext}
                                />
                            )}
                        </div>
                        <GenerationPeriodLabel dateRange={dateRange}/>
                    </div>
                    <div className="flex items-center justify-end min-w-10">
                        {headerActions}
                    </div>
                </div>
            </div>

            <div ref={scrollContainerRef} className="flex-1 overflow-auto">
                {allWeekRows.length === 0 || isEmpty ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        {emptyLabel}
                    </div>
                ) : (
                    <div className={`min-w-full ${strongGrid ? "border-t-2 border-l-2 border-gray-300" : ""}`}>
                        {visibleWeekRows.map(({ weekStart, days }) => (
                            <div
                                key={weekStart.getTime()}
                                className={`grid grid-cols-7 ${strongGrid ? "" : "border-b border-gray-200"}`}
                            >
                                {days.map((day) => (
                                    <div
                                        key={day.dateStr}
                                        className={strongGrid ? "border-r-2 border-b-2 border-gray-300" : "border-r border-gray-200 last:border-r-0"}
                                    >
                                        {renderDayCell(day)}
                                    </div>
                                ))}
                            </div>
                        ))}
                        {hasMoreWeeks && (
                            <div
                                ref={sentinelRef}
                                className="flex items-center justify-center py-4 text-gray-500"
                            >
                                Chargement...
                            </div>
                        )}
                    </div>
                )}
            </div>

            {overlay}
        </div>
    );
};

export default CalendarModalShell;
